import { Capacitor, registerPlugin } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

/**
 * Android-appen (AN1). Webben laddas från qvenari.com i alla appbyggen, så allt här är tyst
 * på iOS, i webbläsaren och i Android-byggen utan pluginet.
 *
 * - androidInfo(): vad bygget kan (fcm = google-services.json fanns när appen byggdes).
 * - Bakåtknappen: öppna lager (Q, menyn …) stängs först, sedan går sidan bakåt, och på
 *   startsidan läggs appen i bakgrunden i stället för att avslutas.
 */

export interface AndroidInfo { fcm: boolean; versionName?: string; versionCode?: number }

/** MainActivity.java registrerar QvenariAndroidPlugin. */
const QvenariAndroid = registerPlugin<{ info(): Promise<AndroidInfo> }>('QvenariAndroid');

export function isAndroidApp(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
}

let info: Promise<AndroidInfo> | null = null;

export function androidInfo(): Promise<AndroidInfo> {
  if (!isAndroidApp() || !Capacitor.isPluginAvailable('QvenariAndroid')) return Promise.resolve({ fcm: false });
  info ??= QvenariAndroid.info().catch(() => ({ fcm: false }));
  return info;
}

// ─── Bakåtknappen ─────────────────────────────────────────────────

/** Returnerar true om den tog hand om trycket (t.ex. stängde ett lager). */
export type BackHandler = () => boolean;

const handlers: BackHandler[] = [];

/** Senast registrerade frågas först. Returnerar en funktion som tar bort hanteraren. */
export function onAndroidBack(h: BackHandler): () => void {
  handlers.push(h);
  return () => {
    const i = handlers.lastIndexOf(h);
    if (i >= 0) handlers.splice(i, 1);
  };
}

/** Vad ett tryck på bakåt gör: 'handled' (ett lager stängdes), 'back' (sidan bakåt) eller 'minimize'. */
export function backAction(canGoBack: boolean): 'handled' | 'back' | 'minimize' {
  for (let i = handlers.length - 1; i >= 0; i--) {
    try {
      if (handlers[i]()) return 'handled';
    } catch { /* nästa */ }
  }
  return canGoBack ? 'back' : 'minimize';
}

let backInstalled = false;

/** En gång vid start (App.tsx). */
export function initAndroidBackButton(): void {
  if (backInstalled || !isAndroidApp()) return;
  backInstalled = true;
  void CapApp.addListener('backButton', ({ canGoBack }) => {
    const a = backAction(canGoBack);
    if (a === 'back') window.history.back();
    else if (a === 'minimize') void CapApp.minimizeApp();
  }).catch(() => { backInstalled = false; });
}
